import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Zap } from "lucide-react";
import { openBillingPortal, startGoogleSignIn } from "./auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EASE = [0.16, 1, 0.3, 1];

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: 49,
    tagline: "For teams validating their outbound motion.",
    features: ["500 AI leads/mo", "Automated outreach", "Email support"],
    highlighted: false,
    testid: "pricing-starter",
  },
  {
    id: "pro",
    name: "Pro",
    price: 99,
    tagline: "For pipelines that never sleep.",
    features: ["Unlimited AI leads", "Priority workflows", "24/7 support"],
    highlighted: true,
    testid: "pricing-pro",
  },
];

export default function Pricing({ user, authChecked }) {
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleCheckout = async (planId) => {
    if (loadingPlan) return;
    if (!user) {
      startGoogleSignIn();
      return;
    }
    setLoadingPlan(planId);
    try {
      const res = await fetch(`${API}/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan: planId,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) throw new Error("checkout failed");
      const data = await res.json();
      window.location.href = data.url;
    } catch {
      setLoadingPlan(null);
    }
  };
  return (
    <section id="pricing" className="relative py-28 md:py-36" data-testid="pricing-section">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[460px] w-[760px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-teal-700/10 blur-[140px]"
      />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-16 text-center"
        >
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.24em] text-teal-300">
            Pricing
          </p>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Simple pricing that scales
            <br className="hidden sm:block" /> with your pipeline
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-slate-400 md:text-lg">
            Start free. Upgrade when the meetings start booking themselves.
          </p>
        </motion.div>

        <div className="mx-auto grid max-w-4xl grid-cols-1 items-stretch gap-8 md:grid-cols-2">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.75, delay: i * 0.15, ease: EASE }}
              className={
                plan.highlighted
                  ? "rounded-2xl bg-gradient-to-b from-blue-500/60 via-cyan-500/50 to-teal-500/60 p-px shadow-[0_0_70px_rgba(45,212,191,0.28)]"
                  : ""
              }
              data-testid={`${plan.testid}-card`}
            >
              <div
                className={`flex h-full flex-col rounded-2xl p-8 md:p-10 ${
                  plan.highlighted
                    ? "bg-[#0A0C1F]"
                    : "border border-white/10 bg-white/[0.03] backdrop-blur-xl"
                }`}
              >
                {plan.highlighted && (
                  <span className="mb-6 flex w-fit items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-600 to-teal-600 px-3.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                    <Zap className="h-3 w-3" />
                    Most Popular
                  </span>
                )}
                <h3 className="font-display text-lg font-semibold text-white">
                  {plan.name}
                </h3>
                <p className="mt-1.5 text-sm text-slate-400">{plan.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1.5">
                  <span
                    className={`font-display text-5xl font-semibold tracking-tight ${
                      plan.highlighted
                        ? "bg-gradient-to-r from-blue-300 via-cyan-300 to-teal-300 bg-clip-text text-transparent"
                        : "text-white"
                    }`}
                  >
                    ${plan.price}
                  </span>
                  <span className="text-sm text-slate-500">/month</span>
                </div>
                <ul className="mt-8 flex-1 space-y-3.5">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full ${
                          plan.highlighted
                            ? "bg-teal-500/20 text-teal-300"
                            : "bg-white/5 text-slate-400"
                        }`}
                      >
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className="text-sm text-slate-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() =>
                    user?.plan_status === "active" && user?.plan !== plan.id
                      ? openBillingPortal()
                      : handleCheckout(plan.id)
                  }
                  disabled={
                    loadingPlan !== null ||
                    (user?.plan === plan.id && user?.plan_status === "active")
                  }
                  className={`mt-10 w-full rounded-full py-3.5 text-sm font-semibold transition-[transform,box-shadow,background-color,color,opacity] duration-300 hover:scale-[1.02] disabled:cursor-default disabled:opacity-70 ${
                    plan.highlighted
                      ? "bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 text-white shadow-[0_0_32px_rgba(34,211,238,0.4)] hover:shadow-[0_0_48px_rgba(45,212,191,0.55)]"
                      : "border border-white/15 bg-white/5 text-white hover:bg-white/10"
                  }`}
                  data-testid={`${plan.testid}-button`}
                >
                  {user?.plan === plan.id && user?.plan_status === "active"
                    ? "Your current plan"
                    : loadingPlan === plan.id
                      ? "Redirecting to Stripe…"
                      : user?.plan_status === "active"
                        ? "Switch plan"
                        : "Start Free Trial"}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10 text-center text-xs font-medium tracking-wide text-slate-500"
        >
          No credit card required &bull; Cancel anytime
        </motion.p>
      </div>
    </section>
  );
}
